// src/components/Notifications.jsx (FULL DROP-IN)
// ✅ Keeps split-view UI + filters + admin support mode
// ✅ Updated for canonical workflow including approved drop-off point path
// ✅ Adds awaiting_dropoff stage support
// ✅ Keeps offer-status enrichment + role-aware routing
// ✅ Preserves recipient-only mark-as-read rule

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  writeBatch,
  limit,
} from "firebase/firestore";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import "./Notifications.css";

import { handleImgError } from "../utils/images";

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const normStr = (v) => String(v || "").trim();
const normLower = (v) => normStr(v).toLowerCase();

const toMillisSafe = (ts) => {
  try {
    if (!ts) return 0;
    if (typeof ts.toMillis === "function") return ts.toMillis();
    if (typeof ts.toDate === "function") return ts.toDate().getTime();
    if (ts instanceof Date) return ts.getTime();
    if (typeof ts === "number") return ts;
    return 0;
  } catch {
    return 0;
  }
};

const cleanOldNotifications = (notifList) => {
  const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
  const now = Date.now();
  return notifList.filter((n) => {
    const ms = toMillisSafe(n.createdAt || n.timestamp);
    if (!ms) return true;
    return now - ms <= THIRTY_DAYS;
  });
};

// -----------------------------
// Workflow-aware helpers
// -----------------------------
const stageLabel = (s) => {
  const k = normLower(s);
  const MAP = {
    seller_prep: "Seller Prep",
    buyer_approval: "Buyer Approval",
    awaiting_payment: "Awaiting Payment",
    ready_for_inspection: "Ready for Inspection",
    awaiting_dropoff: "Awaiting Drop-off",
    inspection_passed: "Inspection Passed",
    pickup_scheduled: "Pickup Scheduled",
    pickup_in_progress: "Pickup In Progress",
    hub_inspection_pass: "Hub Inspection Passed",
    delivery_scheduled: "Delivery Scheduled",
    delivery_in_progress: "Delivery In Progress",
    delivered_pending_buyer_verification: "Delivered (Pending Buyer Verification)",
    verification_passed: "Buyer Verified",
    completed: "Completed",
    cancelled: "Cancelled",
  };
  return MAP[k] || (k ? k.replace(/_/g, " ") : "");
};

const extractWfStage = (n) =>
  normLower(
    n?.wfStage ||
      n?.stage ||
      n?.meta?.wfStage ||
      n?.meta?.stage ||
      n?.meta?.toStage ||
      n?.meta?.nextStage ||
      ""
  );

const extractFulfillmentMethod = (n) =>
  normLower(
    n?.fulfillmentMethod ||
      n?.meta?.fulfillmentMethod ||
      n?.meta?.method ||
      n?.workflow?.fulfillmentChoice?.method ||
      ""
  );

const extractDropoffPartnerId = (n) =>
  normLower(
    n?.dropoffPartnerId ||
      n?.meta?.dropoffPartnerId ||
      n?.meta?.partnerId ||
      n?.workflow?.dropoff?.partnerId ||
      ""
  );

const isPartnerDropoffNotif = (n) => {
  const stage = extractWfStage(n);
  const method = extractFulfillmentMethod(n);
  const partnerId = extractDropoffPartnerId(n);
  const text = normLower(`${n?.title || ""} ${n?.message || ""} ${n?.type || ""}`);
  return (
    method === "partner_dropoff" ||
    partnerId === "approved_dropoff_point" ||
    stage === "awaiting_dropoff" ||
    text.includes("drop-off") ||
    text.includes("dropoff")
  );
};

const isPaymentRequired = (n) => {
  const t = normLower(n?.type || "");
  const st = extractWfStage(n);
  return (
    t.includes("payment_required") ||
    t.includes("awaiting_payment") ||
    st === "awaiting_payment"
  );
};

const isGateStage = (stage) => {
  const s = normLower(stage);
  return (
    s === "seller_prep" ||
    s === "buyer_approval" ||
    s === "awaiting_payment" ||
    s === "ready_for_inspection" ||
    s === "awaiting_dropoff" ||
    s === "inspection_passed" ||
    s === "pickup_scheduled" ||
    s === "pickup_in_progress" ||
    s === "hub_inspection_pass" ||
    s === "delivery_scheduled" ||
    s === "delivery_in_progress" ||
    s === "delivered_pending_buyer_verification" ||
    s === "verification_passed" ||
    s === "completed" ||
    s === "cancelled"
  );
};

const getLinkOverride = (n) =>
  normStr(n?.linkOverride || n?.meta?.linkOverride || n?.link_override || "") ||
  normStr(n?.link || "");

const classifyType = (t) => {
  const type = normLower(t);
  if (!type) return "system";

  if (type.includes("payment_required") || type.includes("awaiting_payment")) return "workflow";

  if (
    type.includes("offer") ||
    type.includes("bid") ||
    type.includes("counter") ||
    type.includes("proposal")
  ) {
    return "offer";
  }

  if (
    type.includes("risk") ||
    type.includes("inspection") ||
    type.includes("pickup") ||
    type.includes("dropoff") ||
    type.includes("drop-off") ||
    type.includes("schedule") ||
    type.includes("prep") ||
    type.includes("verify") ||
    type.includes("commit") ||
    type.includes("delivery") ||
    type.includes("completed") ||
    type.includes("cancel") ||
    type.includes("workflow") ||
    type.includes("logistics") ||
    type.includes("hub")
  ) {
    return "workflow";
  }

  if (type.includes("chat") || type.includes("message") || type.includes("inbox")) return "chat";

  return "system";
};

// ---- Offer status helpers
const normalizeOfferStatus = (s) => {
  const v = normLower(s);
  if (!v) return "";
  if (v.includes("accept")) return "accepted";
  if (v.includes("declin")) return "declined";
  if (v.includes("cancel")) return "cancelled";
  if (v.includes("pend")) return "pending";
  return v;
};

const inferOfferAudience = (rawType) => {
  const t = normLower(rawType);

  if (t.includes("offer_received") || t.includes("new_offer") || t.includes("offer_created"))
    return "seller";

  if (t.includes("offer_accepted") || t.includes("offer_declined") || t.includes("offer_cancelled"))
    return "buyer";

  return "unknown";
};

const endsWithNzAdminDomain = (email) => {
  const e = normLower(email);
  return !!e && e.endsWith("@nexterzone.com");
};

const isAdminActionableNotif = (n) => {
  const t = normLower(n?.type || "");
  const msg = normLower(`${n?.title || ""} ${n?.message || ""}`);
  const stage = extractWfStage(n);

  if (n?.audience === "admin" || n?.recipientRole === "admin" || n?.adminOnly === true) return true;
  if (n?.meta?.audience === "admin" || n?.meta?.recipientRole === "admin" || n?.meta?.adminOnly === true)
    return true;
  if (n?.requiresAdminApproval === true || n?.meta?.requiresAdminApproval === true) return true;

  const isOpsStage =
    stage === "seller_prep" ||
    stage === "ready_for_inspection" ||
    stage === "awaiting_dropoff" ||
    stage === "pickup_scheduled" ||
    stage === "inspection_passed" ||
    stage === "pickup_in_progress" ||
    stage === "hub_inspection_pass" ||
    stage === "delivery_scheduled" ||
    stage === "delivery_in_progress" ||
    stage === "verification_passed";

  const looksActionable =
    t.includes("seller_prep") ||
    t.includes("sellerprep") ||
    t.includes("prep_submitted") ||
    t.includes("awaiting_admin") ||
    t.includes("admin_approval") ||
    t.includes("schedule_pickup") ||
    t.includes("start_pickup") ||
    t.includes("pickup_inspection") ||
    t.includes("hub_inspection") ||
    t.includes("schedule_delivery") ||
    t.includes("mark_delivered") ||
    t.includes("complete_trade") ||
    t.includes("dropoff") ||
    t.includes("drop-off") ||
    msg.includes("awaiting admin") ||
    msg.includes("admin approval") ||
    msg.includes("schedule pickup") ||
    msg.includes("start pickup") ||
    msg.includes("inspection") ||
    msg.includes("drop-off") ||
    msg.includes("dropoff") ||
    msg.includes("schedule delivery") ||
    msg.includes("mark delivered") ||
    msg.includes("complete trade");

  if (!looksActionable && !isOpsStage) return false;

  if (t.includes("offer_") || msg.includes("your offer") || msg.includes("you accepted")) return false;

  return true;
};

const Notifications = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const q = useQuery();

  const isAdmin = !!currentUser?.email && normLower(currentUser.email).endsWith("@nexterzone.com");

  const isSellerConsoleUser = !!currentUser?.uid && !!currentUser?.email && !isAdmin;
  const isBuyerConsoleUser = !isAdmin && !!currentUser?.uid;

  const [notifications, setNotifications] = useState([]);
  const [offerStatusById, setOfferStatusById] = useState({});
  const [selectedTradeId, setSelectedTradeId] = useState("");
  const [selectedOfferId, setSelectedOfferId] = useState("");
  const [adminTradeSearch, setAdminTradeSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const clickLockRef = useRef(false);

  useEffect(() => {
    try {
      const fromQs = q.get("tradeId");
      if (fromQs && typeof fromQs === "string" && fromQs.trim()) {
        setSelectedTradeId(fromQs.trim());
        setAdminTradeSearch(fromQs.trim());
      }
    } catch {}
  }, [loc.search, q]);

  // ------------------------------------------------------------
  // 1) Notifications feed
  // ------------------------------------------------------------
  useEffect(() => {
    if (!currentUser?.uid && !isAdmin) return;

    const notifsCol = collection(db, "Notifications");

    if (isAdmin) {
      const qAll = query(
        notifsCol,
        where("audience", "==", "admin"),
        orderBy("createdAt", "desc"),
        limit(500)
      );

      const unsub = onSnapshot(
        qAll,
        (snapshot) => {
          const merged = snapshot.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((n) => classifyType(n?.type) !== "chat")
            .filter((n) => {
              if (!isAdminActionableNotif(n)) return false;

              const rEmail = n?.recipientEmailLower || n?.recipientEmail || "";
              const rUid = n?.recipientUid || "";
              if (rUid) return false;
              if (rEmail && !endsWithNzAdminDomain(rEmail)) return false;

              const tradeId = normStr(n?.tradeId || n?.meta?.tradeId || n?.trade?.id || "");
              return !!tradeId;
            })
            .sort(
              (x, y) =>
                (toMillisSafe(y.createdAt || y.timestamp || y.updatedAt) || 0) -
                (toMillisSafe(x.createdAt || x.timestamp || x.updatedAt) || 0)
            );

          setNotifications(cleanOldNotifications(merged));
        },
        (err) => {
          console.error("Admin notifications snapshot error (all):", err);
          setNotifications([]);
        }
      );

      return () => unsub();
    }

    const uid = normStr(currentUser.uid);
    const emailRaw = normStr(currentUser.email);
    const emailLower = normLower(emailRaw);

    const qUid = query(
      notifsCol,
      where("recipientUid", "==", uid),
      orderBy("createdAt", "desc"),
      limit(250)
    );

    const qLowerNew = emailLower
      ? query(
          notifsCol,
          where("recipientEmailLower", "==", emailLower),
          orderBy("createdAt", "desc"),
          limit(250)
        )
      : null;

    const qLowerLegacy = emailLower
      ? query(
          notifsCol,
          where("recipientEmail", "==", emailLower),
          orderBy("createdAt", "desc"),
          limit(250)
        )
      : null;

    const qRaw =
      emailRaw && emailRaw !== emailLower
        ? query(
            notifsCol,
            where("recipientEmail", "==", emailRaw),
            orderBy("createdAt", "desc"),
            limit(250)
          )
        : null;

    let unsubUid = null;
    let unsubLowerNew = null;
    let unsubLowerLegacy = null;
    let unsubRaw = null;

    let latestUid = [];
    let latestLowerNew = [];
    let latestLowerLegacy = [];
    let latestRaw = [];

    const mergeAndSet = (a, b, c, d) => {
      const m = new Map();
      for (const n of a) m.set(n.id, n);
      for (const n of b) m.set(n.id, n);
      for (const n of c) m.set(n.id, n);
      for (const n of d) m.set(n.id, n);

      const merged = Array.from(m.values())
        .filter((n) => classifyType(n?.type) !== "chat")
        .sort(
          (x, y) =>
            (toMillisSafe(y.createdAt || y.timestamp || y.updatedAt) || 0) -
            (toMillisSafe(x.createdAt || x.timestamp || x.updatedAt) || 0)
        );

      setNotifications(cleanOldNotifications(merged));
    };

    unsubUid = onSnapshot(
      qUid,
      (snapshot) => {
        latestUid = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

        if (latestUid.length > 0) {
          if (unsubLowerNew) {
            unsubLowerNew();
            unsubLowerNew = null;
            latestLowerNew = [];
          }
          if (unsubLowerLegacy) {
            unsubLowerLegacy();
            unsubLowerLegacy = null;
            latestLowerLegacy = [];
          }
          if (unsubRaw) {
            unsubRaw();
            unsubRaw = null;
            latestRaw = [];
          }
          mergeAndSet(latestUid, [], [], []);
          return;
        }

        if (!qLowerNew && !qLowerLegacy && !qRaw) {
          mergeAndSet([], [], [], []);
          return;
        }

        if (qLowerNew && !unsubLowerNew) {
          unsubLowerNew = onSnapshot(
            qLowerNew,
            (ss) => {
              latestLowerNew = ss.docs.map((d) => ({ id: d.id, ...d.data() }));
              mergeAndSet(latestUid, latestLowerNew, latestLowerLegacy, latestRaw);
            },
            (err) => console.error("Notifications snapshot error (lowerNew):", err)
          );
        }

        if (qLowerLegacy && !unsubLowerLegacy) {
          unsubLowerLegacy = onSnapshot(
            qLowerLegacy,
            (ss) => {
              latestLowerLegacy = ss.docs.map((d) => ({ id: d.id, ...d.data() }));
              mergeAndSet(latestUid, latestLowerNew, latestLowerLegacy, latestRaw);
            },
            (err) => console.error("Notifications snapshot error (lowerLegacy):", err)
          );
        }

        if (qRaw && !unsubRaw) {
          unsubRaw = onSnapshot(
            qRaw,
            (ss) => {
              latestRaw = ss.docs.map((d) => ({ id: d.id, ...d.data() }));
              mergeAndSet(latestUid, latestLowerNew, latestLowerLegacy, latestRaw);
            },
            (err) => console.error("Notifications snapshot error (raw):", err)
          );
        }
      },
      (err) => {
        console.error("Notifications snapshot error (uid):", err);
        setNotifications([]);
      }
    );

    return () => {
      if (unsubUid) unsubUid();
      if (unsubLowerNew) unsubLowerNew();
      if (unsubLowerLegacy) unsubLowerLegacy();
      if (unsubRaw) unsubRaw();
    };
  }, [currentUser?.uid, currentUser?.email, isAdmin]);

  // ------------------------------------------------------------
  // 1b) Offer-status enrichment
  // ------------------------------------------------------------
  useEffect(() => {
    const offerNotifs = notifications
      .filter((n) => classifyType(n?.type) === "offer")
      .slice(0, 60);

    const ids = Array.from(
      new Set(
        offerNotifs
          .map((n) => normStr(n.offerId || n?.meta?.offerId || n?.offer?.id || ""))
          .filter(Boolean)
      )
    ).slice(0, 40);

    if (!ids.length) {
      setOfferStatusById({});
      return;
    }

    let cancelled = false;
    const unsubs = [];

    setOfferStatusById((prev) => {
      const next = {};
      ids.forEach((id) => {
        if (prev[id]) next[id] = prev[id];
      });
      return next;
    });

    ids.forEach((offerId) => {
      try {
        const ref = doc(db, "TradeOfferStatus", offerId);

        const unsub = onSnapshot(
          ref,
          (snap) => {
            if (cancelled) return;

            if (!snap.exists()) {
              setOfferStatusById((prev) => ({
                ...prev,
                [offerId]: { status: "", updatedAtMs: 0 },
              }));
              return;
            }

            const data = snap.data() || {};
            const status = normalizeOfferStatus(data.status || data.offerStatus || data.state || "");
            const updatedAtMs =
              toMillisSafe(data.updatedAt || data.updated_at || data.lastUpdatedAt) || 0;

            setOfferStatusById((prev) => ({
              ...prev,
              [offerId]: { status, updatedAtMs },
            }));
          },
          (err) => {
            console.warn("[Notifications] offer status snapshot error:", offerId, err);
          }
        );

        unsubs.push(unsub);
      } catch (e) {
        console.warn("[Notifications] offer status listener attach failed:", offerId, e);
      }
    });

    return () => {
      cancelled = true;
      unsubs.forEach((fn) => {
        try {
          fn();
        } catch {}
      });
    };
  }, [notifications]);

  // ------------------------------------------------------------
  // 2) Mark-as-read helpers
  // ------------------------------------------------------------
  const markReadBestEffort = async (notif) => {
    try {
      if (isAdmin) return;
      if (!notif?.id) return;
      if (notif.isRead) return;
      await updateDoc(doc(db, "Notifications", notif.id), {
        isRead: true,
        updatedAt: new Date(),
      });
    } catch (e) {
      console.warn("Failed to mark notification read:", e);
    }
  };

  const markAllAsRead = async () => {
    if (isAdmin) return;
    if (!notifications.length) return;
    const batch = writeBatch(db);

    notifications.forEach((n) => {
      if (!n.isRead) {
        batch.update(doc(db, "Notifications", n.id), {
          isRead: true,
          updatedAt: new Date(),
        });
      }
    });

    try {
      await batch.commit();
    } catch (e) {
      console.error("Failed to mark all as read:", e);
    }
  };

  // ------------------------------------------------------------
  // 3) Time + grouping
  // ------------------------------------------------------------
  const formatRelativeTime = (ts) => {
    const ms = toMillisSafe(ts);
    if (!ms) return "";
    const diff = Math.floor((Date.now() - ms) / 1000);
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(ms).toLocaleDateString();
  };

  // ------------------------------------------------------------
  // 3b) UI helpers
  // ------------------------------------------------------------
  const getTradeId = (n) => normStr(n?.tradeId || n?.meta?.tradeId || n?.trade?.id || "");
  const getOfferId = (n) => normStr(n?.offerId || n?.meta?.offerId || n?.offer?.id || "");

  const getOfferStatusForNotif = (n) => {
    const offerId = getOfferId(n);
    const live = offerId ? offerStatusById?.[offerId]?.status : "";
    if (live) return live;

    const metaStatus =
      n?.offerStatus || n?.meta?.offerStatus || n?.meta?.status || n?.offer?.status || "";
    return normalizeOfferStatus(metaStatus);
  };
 
  // ------------------------------------------------------------
  // 4) Lanes + filter
  // ------------------------------------------------------------
  const offersLane = useMemo(
    () => notifications.filter((n) => classifyType(n?.type) === "offer"),
    [notifications]
  );

  const workflowLane = useMemo(
    () => notifications.filter((n) => classifyType(n?.type) === "workflow"),
    [notifications]
  );

  useEffect(() => {
    if (selectedTradeId) return;

    const firstOfferWithTrade = offersLane.find((n) => !!getTradeId(n)) || null;
    const offerTradeId = getTradeId(firstOfferWithTrade);
    if (offerTradeId) {
      setSelectedTradeId(offerTradeId);
      setSelectedOfferId(getOfferId(firstOfferWithTrade) || "");
      if (isAdmin) setAdminTradeSearch(offerTradeId);
      return;
    }

    const firstWorkflowWithTrade = workflowLane.find((n) => !!getTradeId(n)) || null;
    const wfTradeId = getTradeId(firstWorkflowWithTrade);
    if (wfTradeId) {
      setSelectedTradeId(wfTradeId);
      setSelectedOfferId(getOfferId(firstWorkflowWithTrade) || "");
      if (isAdmin) setAdminTradeSearch(wfTradeId);
    }
  }, [offersLane, workflowLane, selectedTradeId, isAdmin]);


  const workflowForSelectedTrade = useMemo(() => {
    if (isAdmin && !selectedTradeId) return workflowLane;
    if (!selectedTradeId) return [];
    return workflowLane
      .filter((n) => getTradeId(n) && normStr(getTradeId(n)) === normStr(selectedTradeId))
      .sort(
        (a, b) =>
          (toMillisSafe(a.createdAt || a.timestamp || a.updatedAt) || 0) -
          (toMillisSafe(b.createdAt || b.timestamp || b.updatedAt) || 0)
      );
  }, [workflowLane, selectedTradeId, isAdmin]);

  // ------------------------------------------------------------
  // 5) Interaction model
  // ------------------------------------------------------------
  const selectNotif = async (notif) => {
    if (!notif) return;

    const tradeId = getTradeId(notif) || "";
    const offerId = getOfferId(notif) || "";

    if (tradeId) {
      setSelectedTradeId(tradeId);
      if (isAdmin) setAdminTradeSearch(tradeId);
    }
    setSelectedOfferId(offerId);

    await markReadBestEffort(notif);
  };

  const resolveLink = useCallback(
    (notif) => {
      const tradeId = getTradeId(notif);
      const offerId = getOfferId(notif);
      const rawType = normLower(notif?.type || "system");
      const bucket = classifyType(rawType);
      const stageHint = extractWfStage(notif);
      const override = getLinkOverride(notif);

      if (isPaymentRequired(notif)) {
        if (override) return override;
        if (tradeId && isBuyerConsoleUser && !isSellerConsoleUser && !isAdmin) {
          return `/checkout/${encodeURIComponent(tradeId)}`;
        }
        if (tradeId) {
          return isAdmin
            ? `/trade/${encodeURIComponent(tradeId)}?tab=workflow`
            : `/trade/${encodeURIComponent(tradeId)}?tab=workflow`;
        }
        return "/market/notifications";
      }

      if (override) return override;

      if (bucket === "workflow" && tradeId) {
        if (isAdmin) {
          return `/trade/${encodeURIComponent(tradeId)}?tab=workflow`;
        }

        if (stageHint === "buyer_approval" && isBuyerConsoleUser && !isSellerConsoleUser) {
          return `/trade/${encodeURIComponent(tradeId)}?tab=workflow`;
        }

        return `/trade/${encodeURIComponent(tradeId)}?tab=workflow`;
      }

      if (bucket === "offer" && tradeId) {
        if (isAdmin || isSellerConsoleUser) {
          return `/market/offers/${encodeURIComponent(tradeId)}${
            offerId ? `?offerId=${encodeURIComponent(offerId)}` : ""
          }`;
        }

        return `/market/offer${
          tradeId || offerId
            ? `?${[
                tradeId ? `tradeId=${encodeURIComponent(tradeId)}` : null,
                offerId ? `offerId=${encodeURIComponent(offerId)}` : null,
              ]
                .filter(Boolean)
                .join("&")}`
            : ""
        }`;
      }

      if (tradeId) return `/trade/${encodeURIComponent(tradeId)}`;
      return "/market/notifications";
    },
    [isAdmin, isSellerConsoleUser, isBuyerConsoleUser]
  );

  const openNotif = async (notif) => {
    if (!notif) return;
    if (clickLockRef.current) return;
    clickLockRef.current = true;
    setTimeout(() => (clickLockRef.current = false), 250);

    await markReadBestEffort(notif);

    const link = resolveLink(notif);
    navigate(link);
  };

  // ------------------------------------------------------------
  // 6) Render helpers
  // ------------------------------------------------------------
 const renderSimpleList = (items = []) => {
  return (
    <div className="notif-simple-list">
      {items.map((n) => {
        const when = n.createdAt || n.timestamp || n.updatedAt;
        const unread = !n.isRead;
        const tradeId = getTradeId(n) || "";
        const offerId = getOfferId(n) || "";
        const bucket = classifyType(n?.type);
        const offerStatus = bucket === "offer" && offerId ? getOfferStatusForNotif(n) : "";
        const stHint = extractWfStage(n);

        const title =
          n.title ||
          (bucket === "offer"
            ? "Offer update"
            : bucket === "workflow"
            ? "Trade update"
            : "Notification");

        const subtext =
          n.message ||
          (bucket === "offer"
            ? "Open to view the latest offer details."
            : "Open to view the latest update.");

        const chips = [];

        chips.push(
          <span key="time" className="notif-time">
            {formatRelativeTime(when)}
          </span>
        );

        chips.push(
          <span key="bucket" className="notif-chip">
            {bucket === "offer" ? "Offer" : bucket === "workflow" ? "Update" : "System"}
          </span>
        );

        if (offerStatus && chips.length < 3) {
          chips.push(
            <span key="offerStatus" className="notif-chip subtle">
              {offerStatus.charAt(0).toUpperCase() + offerStatus.slice(1)}
            </span>
          );
        }

        if (bucket === "workflow" && stHint && chips.length < 3) {
          chips.push(
            <span key="stage" className="notif-chip subtle">
              {stageLabel(stHint)}
            </span>
          );
        }

        if (isPaymentRequired(n) && chips.length < 3) {
          chips.push(
            <span key="payment" className="notif-chip warn">
              Payment Required
            </span>
          );
        }

        if (tradeId && chips.length < 3) {
          chips.push(
            <span key="tradeId" className="notif-chip subtle mono">
              {tradeId.slice(0, 10)}…
            </span>
          );
        }

        return (
          <div
            key={n.id}
            className={`notif-simple-card ${unread ? "unread" : ""}`}
            role="button"
            tabIndex={0}
            onClick={() => selectNotif(n)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") selectNotif(n);
            }}
          >
           {bucket === "offer" || n.itemImage ? (
            <div className="notif-simple-card__media">
              {n.itemImage ? (
                <img
                  src={n.itemImage}
                  alt="Trade item"
                  loading="lazy"
                  onError={handleImgError}
                />
              ) : (
                <div className="notif-simple-card__placeholder" />
              )}
            </div>
          ) : null}

            <div className="notif-simple-card__body">
              <div className="notif-simple-card__top">
                <div className="notif-simple-card__title-wrap">
                  <div className="notif-simple-card__title-row">
                    <h3 className="notif-simple-card__title">{title}</h3>
                    {unread ? <span className="notif-dot" /> : null}
                  </div>

                  <p className="notif-simple-card__text">{subtext}</p>
                </div>

                <button
                  type="button"
                  className="notif-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    openNotif(n);
                  }}
                >
                  Open
                </button>
              </div>

              <div className="notif-simple-card__meta">{chips}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

  // ------------------------------------------------------------
  // 7) Render
  // ------------------------------------------------------------
  return (
  <div className="notif-page">
    <div className="notif-shell notif-shell--simple">
      <div className="notif-head notif-head--simple">
        <div className="notif-head-left">
          <h2 className="notif-title">Notifications</h2>
          <p className="notif-subtitle">
            View your latest offers and trade updates in one simple feed.
          </p>

          <div
            className="notif-filters"
            role="tablist"
            aria-label="Notification filters"
          >
            {[
              { key: "all", label: "All" },
              { key: "offer", label: "Offers" },
              { key: "workflow", label: "Updates" },
            ].map((c) => (
              <button
                key={c.key}
                type="button"
                className={`notif-chip-btn ${filter === c.key ? "active" : ""}`}
                onClick={() => setFilter(c.key)}
                aria-selected={filter === c.key}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="notif-head-right">
          <button
            type="button"
            className="notif-btn"
            onClick={markAllAsRead}
            disabled={isAdmin}
          >
            Mark All as Read
          </button>
        </div>
      </div>

      {notifications.length === 0 ? (
        <div className="notif-empty">
          <img src="/empty-inbox.svg" alt="No notifications" />
          <p>No notifications yet.</p>
        </div>
      ) : (
        <div className="notif-simple-feed">
          {filter === "offer"
            ? renderSimpleList(offersLane)
            : filter === "workflow"
            ? renderSimpleList(
                isAdmin && !selectedTradeId ? workflowLane : workflowForSelectedTrade
              )
            : renderSimpleList(notifications)}
        </div>
      )}
    </div>
  </div>
);
};

export default Notifications;